import { hubDb } from '@shared/storage/db';
import { MissioInvoiceRecord, MissioMissionRecord } from '@shared/domain/missioBridge';
import { UserProfile } from '@shared/types';
import { useLiveQuery } from 'dexie-react-hooks';

const LEGACY_MISSIO_KEY = 'indxone.missio.v1';

function readLegacyStoreRaw(): { invoices?: Array<Record<string, unknown>> } {
  try {
    const raw = localStorage.getItem(LEGACY_MISSIO_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as { invoices?: Array<Record<string, unknown>> };
  } catch {
    return {};
  }
}

function normalizeLegacyInvoice(invoice: Record<string, unknown>): MissioInvoiceRecord {
  return {
    id: String(invoice.id || ''),
    missionId: invoice.missionId ? String(invoice.missionId) : null,
    number: String(invoice.number || ''),
    client: String(invoice.client || ''),
    title: String(invoice.titre || invoice.missionTitre || ''),
    missionTitle: String(invoice.missionTitre || invoice.titre || ''),
    amount: Number(invoice.montantTTC || invoice.montant || 0),
    amountHT: Number(invoice.montantHT || 0),
    amountTTC: Number(invoice.montantTTC || invoice.montant || 0),
    date: String(invoice.date || ''),
    status: String(invoice.statut || 'brouillon'),
  };
}

export async function migrateLegacyInvoicesIfNeeded(): Promise<void> {
  const count = await hubDb.missioInvoices.count();
  if (count > 0) return;

  const raw = readLegacyStoreRaw();
  const legacy = raw.invoices || [];
  if (!legacy.length) return;

  const normalized = legacy.map(normalizeLegacyInvoice).filter((inv) => inv.id);
  if (normalized.length) {
    await hubDb.missioInvoices.bulkPut(normalized);
  }
}

export function useMissioInvoices() {
  return useLiveQuery(
    async () => {
      return hubDb.missioInvoices.toArray();
    },
    [],
    [] as MissioInvoiceRecord[],
  );
}

export function useMissioInvoicesByMission(missionId: string | undefined | null) {
  return useLiveQuery(
    async () => {
      if (!missionId) return [] as MissioInvoiceRecord[];
      return hubDb.missioInvoices.where('missionId').equals(missionId).toArray();
    },
    [missionId],
    [] as MissioInvoiceRecord[],
  );
}

export async function listMissioInvoices(): Promise<MissioInvoiceRecord[]> {
  return hubDb.missioInvoices.toArray();
}

export async function upsertMissioInvoice(record: MissioInvoiceRecord): Promise<MissioInvoiceRecord> {
  await migrateLegacyInvoicesIfNeeded();
  await hubDb.missioInvoices.put(record);
  return record;
}

export async function removeMissioInvoice(id: string): Promise<void> {
  await migrateLegacyInvoicesIfNeeded();
  await hubDb.missioInvoices.delete(id);
}

export async function createMissioDraftInvoice(options: {
  mission: MissioMissionRecord;
  profile: UserProfile;
}): Promise<MissioInvoiceRecord> {
  await migrateLegacyInvoicesIfNeeded();
  const existing = await hubDb.missioInvoices.toArray();
  const today = new Date().toISOString().slice(0, 10);
  const nextNumber = `INV-${today.replace(/-/g, '')}-${String(existing.length + 1).padStart(3, '0')}`;
  const subtotal = Math.round(options.mission.budgetHT || options.mission.tjm * options.mission.joursEstimes || 0);
  const tvaRate = options.profile.status === 'ei' ? 0 : 0.2;
  const total = Math.round(subtotal * (1 + tvaRate));

  const invoice: MissioInvoiceRecord = {
    id: `inv_${Date.now()}`,
    missionId: options.mission.id,
    number: nextNumber,
    client: options.mission.clientNom || options.profile.company || '',
    title: options.mission.titre,
    missionTitle: options.mission.titre,
    amount: total,
    amountHT: subtotal,
    amountTTC: total,
    date: today,
    status: 'brouillon',
  };

  await hubDb.missioInvoices.add(invoice);
  return invoice;
}

export async function updateMissioInvoiceStatus(invoiceId: string, status: 'brouillon' | 'envoyee' | 'payee'): Promise<void> {
  await migrateLegacyInvoicesIfNeeded();
  const invoice = await hubDb.missioInvoices.get(invoiceId);
  if (!invoice) return;
  await hubDb.missioInvoices.put({ ...invoice, status });
}
